<template>
    <div class="w-100">
        <v-card-title class="title font-weight-regular justify-space-between">
            {{
                "Veuillez préciser le moyen de paiement du montant de " +
                newCommonFormatCurrency(totalPersonalisedAmountSigned, undefined)
            }}
            <v-avatar
                color="primary-lighten-2"
                class="subheading white--text"
                size="24"
                v-if="step"
            >
                {{ step }}
            </v-avatar>
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="6">
                    <v-select-filled
                        v-model="paymentType"
                        :items="paymentTypes"
                        item-value="value"
                        label="Type de paiement"
                        density="compact"
                        :item-title="(item) => item.text"
                    />
                </v-col>
                <v-col>
                    <v-text-field-filled
                        :label="paymentType === 'CHEQUE' ? 'Numéro de chèque*' : 'Description*'"
                        v-model="description"
                        :rules="[
                            (v) =>
                                v.length >= 3 ||
                                (paymentType === 'CHEQUE'
                                    ? 'Numéro de chèque requis'
                                    : 'Description requise'),
                        ]"
                        data-cy="CheckImport-numero"
                        density="compact"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col :cols="needsBankAccount ? 6 : 12">
                    <oss-date-picker-filled
                        v-model="date"
                        label="Date"
                        :textFieldReadOnly="false"
                        prefixDataCy="Remittance-"
                    />
                </v-col>
                <v-col cols="6" v-if="needsBankAccount">
                    <v-select
                        v-model="bankAccountId"
                        :items="bankAccounts"
                        item-value="id"
                        label="Compte bancaire utilisé ?"
                        data-cy="Remittance-bankAccountId"
                        density="compact"
                        :item-title="(item) => item.name"
                    />
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions v-if="!actions">
            <v-btn rounded @click="cancel()" class="mx-auto" size="small">
                {{ step ? "Précédent" : "Annuler" }}
            </v-btn>
            <v-spacer />
            <v-btn rounded @click="validate" :loading="loading" size="small">
                Oui, je suis sûr(e)
            </v-btn>
        </v-card-actions>
    </div>
</template>
<script lang="ts">
import { newCommonFormatCurrency } from "../../../../backend/src/common/services/mathServices";
import { Apollo } from "@/decorators";

import BANK_ACCOUNTS from "../../graphql/bank/BankAccounts.gql";
import CREATE_RECONCILIATION_WHITH_REMITTANCE from "../../graphql/reconciliation/CreateReconciliationWithRemittance.gql";
import { getDBFormattedDate } from "@/services/dateServices";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";

@Component({
    emits: ["added", "close", "cancel"],
})
export class Remittance extends Vue {
    @Prop({ default: "" }) accountableAccountName: string;
    @Prop({ default: null }) step: any;
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: null }) accountingLine: any;
    @Prop({ default: null }) accountingLines: any;
    @Prop({ default: null }) actions: any;

    loading: boolean = false;
    description: string = "";
    date: string = "";

    bankAccountId: string = "";

    paymentType: string = "CHEQUE";

    paymentTypes = [
        { text: "Chèque", value: "CHEQUE" },
        { text: "Prélèvement", value: "DIRECT_DEBIT" },
        { text: "Virement", value: "BANK_TRANSFER" },
    ];

    get accountingLines_() {
        if (this.accountingLines) {
            return this.accountingLines;
        }
        if (this.accountingLine) {
            return [
                {
                    id: this.accountingLine.id,
                    amountSigned: this.accountingLine.amountSigned,
                    stillOwe: this.accountingLine.amountSigned,
                    personalisedAmountSigned: this.accountingLine.amountSigned,
                },
            ];
        }
        debugger; // git-ignore
        return [];
    }

    newCommonFormatCurrency(value, options = undefined) {
        return newCommonFormatCurrency(value, undefined);
    }

    beforeMount() {
        if (this.actions) {
            this.actions.action.title = "Valider";
            this.actions.action.function = this.validate;
            this.actions.action.disabled = this.actionDisabled;
            this.actions.action.loading = () => {
                return this.loading;
            };
        }
    }

    async validate() {
        this.loading = true;
        if (this.accountingLines_?.length) {
            const result = await this.$apollo.mutate({
                mutation: CREATE_RECONCILIATION_WHITH_REMITTANCE,
                variables: {
                    companyId: this.companyId,
                    // accountingLineId: this.accountingLine.id,
                    paymentType: this.paymentType,
                    accountingLines: this.accountingLines_,
                    description: this.description,
                    date: getDBFormattedDate(this.date),
                    bankAccountId: this.needsBankAccount ? this.bankAccountId : undefined,
                },
            });
            this.$emit("added", result);
        }
        this.loading = false;
        this.$emit("close");
    }

    cancel() {
        this.$emit("cancel");
    }

    actionDisabled() {
        if (!this.totalPersonalisedAmountSigned) {
            return true;
        }
        if (!this.accountableAccountName) {
            return true;
        }
        if (this.needsBankAccount && !this.bankAccountId) {
            return true;
        }
        // if (this.paymentType === "CHEQUE") {
        return this.description.length < 3 || !this.date;
        // }
        return false;
    }

    get totalPersonalisedAmountSigned() {
        let total = 0;
        for (const al of this.accountingLines_) {
            total += al.personalisedAmountSigned;
        }
        return total;
    }

    get totalAmountSigned() {
        let total = 0;
        for (const al of this.accountingLines_) {
            total += al.amountSigned;
        }
        return total;
    }

    get needsBankAccount() {
        if (!this.accountableAccountName) {
            return false;
        }
        if (this.accountableAccountName.startsWith("411")) {
            return false;
        }
        return true;
    }

    @Apollo({
        query: BANK_ACCOUNTS,
        variables() {
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return !this.companyId || !this.needsBankAccount;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.bankAccounts?.length) {
                    this.bankAccounts = data.bankAccounts;
                    if (this.bankAccounts.length === 1) {
                        this.bankAccountId = this.bankAccounts[0].id;
                    }
                } else {
                    this.bankAccounts = [];
                }
            }
        },
    })
    bankAccounts = [];
}

export default toNative(Remittance);
</script>
